/* eslint-disable @typescript-eslint/member-ordering */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { skip, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import { cloneDeep, sortBy } from 'lodash';

import {
  AppFilter,
  AppTable,
  AppTableColumn,
  Audit,
  CommonDataModel,
  DayPartingValue,
  JapiQuery,
  CtrlUser, ResponseFromServer, Country,
  UserType
} from '../../_models/models';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- deactivation reason formatting is publisher-domain logic; the shared audit component is publisher-aware (see adjustPublisherAuditFields).
import { formatPublisherDeactivationReason } from '../../../features/supply/publishers/_pipes/publisher-deactivation-reason.pipe';
import { ApiService } from '../../_services/api.service';
import { SharedService } from '../../_services/shared.service';
import {
  auditEndpoints,
  auditFiltersConfig,
  auditHistoryWarningBox,
  auditTableButtons,
  AuditTranslatableEntities,
  applyPaDiscountValueToUiMapping,
  auctionTypeValueToUiMapping,
  buyerTypeValueToUiMapping,
  dealFieldNameToUiMapping,
  dealPackageFieldNameToUiMapping,
  dealTypeValueToUiMapping,
  rewardedValueToUiMapping,
  publisherAuditTableColumns,
  trafficSourceAuditTableColumns,
  publisherAuditTableConfig,
  publisherFieldsToUiMapping,
  trafficSourceFieldsToUiMapping,
  dealPartnerSubEntityFieldsToUiMapping,
  getAuditFiltersConfig,
  emptyAuditTranslatableEntitiesObject,
  CONTENT_TARGETING_FIELD_VALUE,
  CONTENT_TARGETING_FIELD_NAMES,
  AuditFieldTranslationConfig,
  countryTranslationConfig
} from './audit.config';
import { NzTreeNode } from 'ng-zorro-antd/tree';
import { SharedStoreService } from '../../_services/shared-store.service';
import { sharedAssetsConfigs } from '../../_services/shared-store.config';
import { Common } from '../../_common/common.helper';
import { convertMinutesToHours, formatHourLabel } from '../day-parting/_utils/time.utils';

@Component({
  selector: 'app-audit',
  templateUrl: './audit.component.html',
  styleUrls: ['./audit.component.less']
})
export class AuditComponent implements OnInit, OnDestroy {

  @Input({ required: true }) auditType: 'PUBLISHER' | 'DEAL' | 'DEAL_PACKAGE' | 'DEAL_PARTNER' | 'TRAFFIC_SOURCE';
  @Input() publisherId: number;
  @Input() entityPublisherId: number;
  @Input() userType: UserType;
  @Input() auditTranslatableEntities: AuditTranslatableEntities;

  audit$: Observable<CommonDataModel<Audit[]>>;
  audit = new CommonDataModel<Audit[]>();
  auditFilters: AppFilter[] = cloneDeep(auditFiltersConfig);
  auditTableColumns: AppTableColumn[];
  auditTable: AppTable;
  auditTableButtons = auditTableButtons;
  readonly auditHistoryWarningBox = auditHistoryWarningBox;

  TRANSLATABLE_ENTITIES_KEYS: string[] = ['bcat', 'btype', 'battr', 'dspAccountExcludeList', 'dealPartnerExcludeList', 'cat', 'country'];
  GEO_FLOORS_ALL_COUNTRIES = '**';
  DAY_PARTING_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  private unsubscribe$ = new Subject<void>();
  private publisherFieldsToUiMapping = publisherFieldsToUiMapping;
  private trafficSourceFieldsToUiMapping = trafficSourceFieldsToUiMapping;
  private entityUserFilterId: string;
  private deviceTypesMap: { [key: string]: string } = {};
  private contentAttributeMaps: { [attribute: string]: { [key: string]: string } } = {
    genre: {},
    contentrating: {},
    language: {}
  };

  private readonly dealPackageFieldToContentAttribute: { [field: string]: string } = {
    filterGenreList: 'genre',
    filterContentRatingList: 'contentrating',
    filterContentLanguageList: 'language'
  };

  constructor(private apiService: ApiService,
    private sharedService: SharedService,
    private sharedStoreService: SharedStoreService,
    private common: Common) {
  }

  ngOnInit(): void {
    this.initializeAuditConfiguration();
    this.loadCountries();
    if (this.auditType === 'DEAL_PACKAGE') {
      this.loadDeviceTypes();
      this.loadContentAttributes();
    }
    this.loadAudit();
    this.adjustFiltersByPortal();
    this.loadUserFilters();
  }

  get auditColumnsStorageKey(): string {
    return `audit:${this.auditType}`;
  }

  private initializeAuditConfiguration(): void {
    this.entityUserFilterId = this.getEntityUserFilterId();
    this.auditTableColumns = this.getAuditTableColumns();
    this.auditTable = { ...publisherAuditTableConfig };
    this.auditFilters = cloneDeep(getAuditFiltersConfig(this.auditType));
    this.auditTranslatableEntities = this.auditTranslatableEntities || cloneDeep(emptyAuditTranslatableEntitiesObject);
    this.flattenDspAccounts();
  }

  private flattenDspAccounts(): void {
    const container = this.auditTranslatableEntities?.dspAccounts;
    this.auditTranslatableEntities.dspAccountsFlattened = [
      ...(container?.activeDspAccountList || []),
      ...(container?.inactiveDspAccountList || [])
    ];
  }

  private getEntityUserFilterId(): string {
    switch (this.auditType) {
      case 'DEAL_PARTNER':
        return 'auditPartnerUser';
      case 'TRAFFIC_SOURCE':
        return undefined;
      default:
        return 'auditPublisherUser';
    }
  }

  private getAuditTableColumns(): AppTableColumn[] {
    switch (this.auditType) {
      case 'TRAFFIC_SOURCE':
        return cloneDeep(trafficSourceAuditTableColumns);
      default:
        return cloneDeep(publisherAuditTableColumns);
    }
  }

  private loadCountries(): void {
    this.sharedStoreService.getSharedAsset(sharedAssetsConfigs.countries.key)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: ResponseFromServer<Country[]>) => {
        if (this.auditTranslatableEntities) {
          this.auditTranslatableEntities.countries = res?.data;
        }
      });
  }

  private loadDeviceTypes(): void {
    this.sharedStoreService.getSharedEnum('DeviceType')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((deviceTypes: { [key: string]: string }) => {
        this.deviceTypesMap = Object.keys(deviceTypes || {}).reduce((res, key) => {
          res[key.toLowerCase()] = deviceTypes[key];
          return res;
        }, {} as { [key: string]: string });
      });
  }

  private loadContentAttributes(): void {
    this.apiService.getContentAttributes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: { data: Array<{ attribute: string; value: string }> }) => {
        const data = res?.data || [];
        Object.keys(this.contentAttributeMaps).forEach(attribute => {
          this.contentAttributeMaps[attribute] = data
            .filter(item => item.attribute === attribute)
            .reduce((acc, item) => {
              acc[item.value.toLowerCase()] = this.capitalizeFirst(item.value);
              return acc;
            }, {} as { [key: string]: string });
        });
      });
  }

  private capitalizeFirst(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
  }

  private capitalizeListValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(item => (typeof item === 'string' ? this.capitalizeFirst(item) : item));
    }
    if (typeof value === 'string' && value) {
      return value.split(',').map(item => this.capitalizeFirst(item.trim())).join(', ');
    }
    return value;
  }

  private loadUserFilters(): void {
    if (this.auditType !== 'TRAFFIC_SOURCE') {
      this.loadUsers('ENTITY');
    }
    if (this.userType === 'INTERNAL') {
      this.loadUsers('INTERNAL');
    }
  }

  public adjustFiltersByPortal(): void {
    if (this.userType !== 'INTERNAL') {
      const findIndex = this.auditFilters.findIndex(f => f.id === 'auditInternalUser');
      if (findIndex > -1) {
        this.auditFilters[findIndex].isHidden = true;
      }
    }
  }

  public onFiltersChange(event: { filters: AppFilter[]; filterId: string }, isResetFilters = false): void {
    const newFilters = isResetFilters
      ? this.handleResetFilters()
      : this.handleFilterUpdate(event);

    this.auditFilters = cloneDeep(newFilters);

    // FUS-3573: Prevent API call with invalid dates (fromDate=0&toDate=0) when required filters are cleared
    if (this.hasInvalidRequiredFilters()) {
      // Show notification only when user actively clears a required field (not on reset)
      if (!isResetFilters) {
        this.sharedService.showNotification('error', 'Invalid filters', 'Please ensure all required filters are filled');
      }
      return;
    }

    this.triggerAuditLoad();
  }

  private handleResetFilters(): AppFilter[] {
    let filters = this.sharedService.resetFilterSelectedValues(cloneDeep(this.auditFilters));

    // Validate required filters after reset
    // resetFilterSelectedValues sets isValid: true for all filters, but required filters
    // without default values need to be marked invalid to prevent API calls with empty data
    filters.forEach(filter => {
      if (filter.isRequired) {
        filter.isValid = this.isRequiredFilterValid(filter);
      }
    });
    filters = this.sharedService.updateFilter(filters, this.entityUserFilterId, 'isDisabled', false);
    filters = this.sharedService.updateFilter(filters, 'auditInternalUser', 'isDisabled', false);

    return filters;
  }

  private handleFilterUpdate(event: { filters: AppFilter[]; filterId: string }): AppFilter[] {
    let filters = event.filters;
    const filterIndex = this.sharedService.getFilterItemIndex(filters, event.filterId);
    const currentFilter = filters[filterIndex];

    if (currentFilter.isRequired) {
      filters = this.updateRequiredFilterValidation(filters, event.filterId);
    }

    filters = this.handleUserFilterIsolation(filters, event.filterId, currentFilter);

    return filters;
  }

  private updateRequiredFilterValidation(filters: AppFilter[], filterId: string): AppFilter[] {
    const filterIndex = this.sharedService.getFilterItemIndex(filters, filterId);
    const filter = filters[filterIndex];
    const isValid = this.isRequiredFilterValid(filter);

    let updatedFilters = this.sharedService.updateFilter(filters, filterId, 'isValid', isValid);
    updatedFilters = this.sharedService.updateFilter(updatedFilters, filterId, 'errorTooltip', 'Required');

    return updatedFilters;
  }

  private handleUserFilterIsolation(
    filters: AppFilter[],
    filterId: string,
    currentFilter: AppFilter
  ): AppFilter[] {
    const hasSelection = currentFilter.selectedValues.length > 0;
    const targetFilterId = filterId === this.entityUserFilterId
      ? 'auditInternalUser'
      : filterId === 'auditInternalUser'
        ? this.entityUserFilterId
        : null;

    return targetFilterId
      ? this.applyFilterIsolation(filters, targetFilterId, hasSelection)
      : filters;
  }

  private applyFilterIsolation(filters: AppFilter[], targetFilterId: string, disable: boolean): AppFilter[] {
    let updatedFilters = filters;

    if (disable) {
      updatedFilters = this.sharedService.clearSelectedValues(updatedFilters, targetFilterId);
    }

    updatedFilters = this.sharedService.updateFilter(updatedFilters, targetFilterId, 'isDisabled', disable);

    return updatedFilters;
  }

  private isRequiredFilterValid(filter: AppFilter): boolean {
    if (!filter.selectedValues) {
      return false;
    }
    // Date range and multi-select filters use arrays
    if (Array.isArray(filter.selectedValues)) {
      return filter.selectedValues.length > 0 && filter.selectedValues.every(v => v != null);
    }
    // Single-select filters use single values
    return filter.selectedValues !== null && filter.selectedValues !== undefined;
  }

  private hasInvalidRequiredFilters(): boolean {
    return this.auditFilters.some(filter => {
      if (!filter.isRequired) {
        return false;
      }

      const isValid = this.isRequiredFilterValid(filter);

      // Date range requires exactly 2 values
      if (filter.id === 'auditDatesRange' && Array.isArray(filter.selectedValues)) {
        return !isValid || filter.selectedValues.length !== 2;
      }

      return !isValid;
    });
  }

  private triggerAuditLoad(): void {
    // Reset to first page when filters change
    this.auditTable.pageIndex = 1;
    if (!this.auditTable.sortBy) {
      this.auditTable.sortBy = 'timestamp';
      this.auditTable.sortDirection = 'DESC';
    }
    this.loadAudit();
  }

  loadAudit(): void {
    this.audit.isLoading = true;
    const query = this.sharedService.buildTableQuery(this.auditFilters, this.auditTable);
    this.expandContentTargetingFieldFilter(query);
    const endpoint = auditEndpoints[this.auditType] + this.publisherId + '/audit';

    // Prevent API call if required filters are invalid (silent check for initialization)
    if (this.hasInvalidRequiredFilters()) {
      this.audit.isLoading = false;
      return;
    }

    const datesRange = this.sharedService.getFilterSelectedValues(this.auditFilters, 'auditDatesRange');
    const fromDate = this.sharedService.convertDateToNumberYYYYMMDD(datesRange[0], true);
    const toDate = this.sharedService.convertDateToNumberYYYYMMDD(datesRange[1], true);
    this.audit$ = this.apiService.getAuditData(query, endpoint, fromDate, toDate).pipe(takeUntil(this.unsubscribe$));
    this.audit$.pipe(skip(1)).subscribe(res => {
      this.audit = this.adjustResponse(res);
      if (res.status === 'Failed') {
        this.sharedService.showNotification('error', 'Failure',
          res.httpErr?.error?.message || 'Action log fetch failed');
      }
    });
  }

  private expandContentTargetingFieldFilter(query: JapiQuery): void {
    const fieldFilter = query?.filter?.filters?.find(filter => filter.fieldName === 'field');
    if (!fieldFilter || !Array.isArray(fieldFilter.value) || !fieldFilter.value.includes(CONTENT_TARGETING_FIELD_VALUE)) {
      return;
    }
    fieldFilter.value = fieldFilter.value.flatMap(value =>
      value === CONTENT_TARGETING_FIELD_VALUE ? CONTENT_TARGETING_FIELD_NAMES : [value]
    );
  }

  private adjustResponse(auditOriginal: CommonDataModel<Audit[]>): CommonDataModel<Audit[]> {
    const adjustedAuditData: Audit[] = auditOriginal.data?.map(val => this.adjustUiValues(val));
    return { ...auditOriginal, data: adjustedAuditData };
  }

  private adjustUiValues(val: Audit): Audit {
    if (this.auditType !== 'DEAL_PARTNER' && !this.auditTranslatableEntities.iabCategoriesFlattened) {
      this.auditTranslatableEntities.iabCategoriesFlattened = this.translateIabCategories(this.auditTranslatableEntities.iabCategories);
    }
    const newVal = this.adjustAuditFieldsByType(val);
    return this.formatAuditValues(newVal);
  }

  private adjustAuditFieldsByType(val: Audit): Audit {
    switch (this.auditType) {
      case 'PUBLISHER':
        return this.adjustPublisherAuditFields(val);
      case 'TRAFFIC_SOURCE':
        return this.adjustTrafficSourceAuditFields(val);
      case 'DEAL_PACKAGE':
        return this.adjustDealPackageAuditFields(val);
      case 'DEAL':
        return this.adjustDealAuditFields(val);
      case 'DEAL_PARTNER':
        return this.adjustDealPartnerAuditFields(val);
      default:
        return { ...val };
    }
  }

  /**
   * `parent` arrives as "<package.>Type/id"; the type picks the label, and the parent is shown as "Type: name"
   * using the `parentName` the API resolves (portfolio name / user email), or "Type id" when no name came back.
   */
  private adjustDealPartnerAuditFields(val: Audit): Audit {
    const [typePath, id] = (val.parent ?? '').split('/');
    const entityType = typePath.split('.').pop();
    const fieldName = dealPartnerSubEntityFieldsToUiMapping[entityType]?.[val.fieldName] ?? val.fieldName;
    return { ...val, fieldName, parent: this.parentLabel(entityType, id, val) };
  }

  /** Keeps the raw `parent` when it isn't in the "Type/id" form. */
  private parentLabel(entityType: string, id: string, val: Audit): string {
    if (!entityType || !id) {
      return val.parent;
    }
    return val.parentName ? `${entityType}: ${val.parentName}` : `${entityType} ${id}`;
  }

  private adjustDealAuditFields(val: Audit): Audit {
    const newVal = { ...val };
    if (newVal.fieldName === 'dealStart' || newVal.fieldName === 'dealEnd') {
      newVal.previousValue = this.convertGmtValueToEst(newVal.previousValue);
      newVal.newValue = this.convertGmtValueToEst(newVal.newValue);
    }
    if (newVal.fieldName === 'dayParting') {
      newVal.previousValue = this.formatDayPartingValue(newVal.previousValue);
      newVal.newValue = this.formatDayPartingValue(newVal.newValue);
    }
    if (newVal.fieldName === 'bidfloorCurrency') {
      newVal.preserveValueCase = true;
    }
    if (newVal.fieldName === 'applyPaDiscount') {
      newVal.previousValue = this.mapBooleanValue(newVal.previousValue, applyPaDiscountValueToUiMapping);
      newVal.newValue = this.mapBooleanValue(newVal.newValue, applyPaDiscountValueToUiMapping);
      newVal.preserveValueCase = true;
    }
    const valueMap = this.getDealValueMap(newVal.fieldName);
    if (valueMap) {
      newVal.previousValue = this.mapValue(newVal.previousValue, valueMap);
      newVal.newValue = this.mapValue(newVal.newValue, valueMap);
    }
    newVal.fieldName = dealFieldNameToUiMapping[newVal.fieldName] || newVal.fieldName;
    return newVal;
  }

  private convertGmtValueToEst(value: unknown): unknown {
    if (typeof value !== 'string' || !value) {
      return value;
    }
    return this.common.convertGmtToEstDateTime(value);
  }

  private getDealValueMap(fieldName: string): { [key: string]: string } | null {
    switch (fieldName) {
      case 'dealType':
        return dealTypeValueToUiMapping;
      case 'auctionType':
        return auctionTypeValueToUiMapping;
      case 'buyerType':
        return buyerTypeValueToUiMapping;
      default:
        return null;
    }
  }

  private mapValue(value: unknown, valueMap: { [key: string]: string }): unknown {
    if (typeof value !== 'string' || !value) {
      return value;
    }
    return valueMap[value.toLowerCase()] || value;
  }

  private mapBooleanValue(value: unknown, valueMap: { [key: string]: string }): unknown {
    if (typeof value === 'boolean') {
      return valueMap[String(value)];
    }
    return this.mapValue(value, valueMap);
  }

  private formatDayPartingValue(value: unknown): unknown {
    const parsed = this.parseDayPartingValue(value);
    if (!parsed?.length) {
      return value;
    }
    return parsed.map(entry => this.formatDayPartingEntry(entry)).join('; ');
  }

  private parseDayPartingValue(value: unknown): DayPartingValue[] | null {
    if (Array.isArray(value)) {
      return value as DayPartingValue[];
    }
    if (typeof value === 'string' && value.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private formatDayPartingEntry(entry: DayPartingValue): string {
    const days = (entry.days || []).map(day => this.DAY_PARTING_DAYS[day] ?? day).join(', ');
    const start = formatHourLabel(convertMinutesToHours(entry.startMinute));
    const end = formatHourLabel(convertMinutesToHours(entry.endMinute));
    return `${days}: ${start} - ${end}`;
  }

  private adjustDealPackageAuditFields(val: Audit): Audit {
    const newVal = { ...val };
    this.translateDealPackageValues(newVal);
    newVal.fieldName = dealPackageFieldNameToUiMapping[newVal.fieldName] || newVal.fieldName;
    return newVal;
  }

  private translateDealPackageValues(newVal: Audit): void {
    if (newVal.fieldName === 'filterRewarded') {
      newVal.previousValue = this.mapValue(newVal.previousValue, rewardedValueToUiMapping);
      newVal.newValue = this.mapValue(newVal.newValue, rewardedValueToUiMapping);
      return;
    }
    if (newVal.fieldName === 'filterDeviceType') {
      newVal.previousValue = this.translateJsonObjectValue(newVal.previousValue, this.deviceTypesMap);
      newVal.newValue = this.translateJsonObjectValue(newVal.newValue, this.deviceTypesMap);
      return;
    }
    if (newVal.fieldName === 'filterCountryList') {
      newVal.previousValue = this.translateJsonObjectValueByConfig(newVal.previousValue, countryTranslationConfig);
      newVal.newValue = this.translateJsonObjectValueByConfig(newVal.newValue, countryTranslationConfig);
      return;
    }
    if (newVal.fieldName === 'filterRegionList') {
      newVal.previousValue = this.capitalizeListValue(newVal.previousValue);
      newVal.newValue = this.capitalizeListValue(newVal.newValue);
      return;
    }
    const contentAttribute = this.dealPackageFieldToContentAttribute[newVal.fieldName];
    if (contentAttribute) {
      const map = this.contentAttributeMaps[contentAttribute] || {};
      newVal.previousValue = this.translateJsonObjectValue(newVal.previousValue, map);
      newVal.newValue = this.translateJsonObjectValue(newVal.newValue, map);
    }
  }

  private translateJsonObjectValueByConfig(value: unknown, configObj: AuditFieldTranslationConfig): unknown {
    const parsed = this.parseJsonObjectValue(value);
    if (parsed === null) {
      return value;
    }
    const matchList = this.auditTranslatableEntities[configObj.matchListName] || [];
    const keys = Object.keys(parsed).map(key => configObj.normalizeKey ? configObj.normalizeKey(key) : key);
    const names = this.getNamesByIds(keys, matchList, configObj.matchProp, configObj.nameProp);
    const uniqueNames = names.filter((name, index) => name && names.indexOf(name) === index);
    return uniqueNames.length ? uniqueNames.join(', ') : 'null';
  }

  private translateJsonObjectValue(value: unknown, displayNameMap: { [key: string]: string }): unknown {
    const parsed = this.parseJsonObjectValue(value);
    if (parsed === null) {
      return value;
    }
    const names = Object.keys(parsed)
      .map(key => displayNameMap[key.toLowerCase()] || this.capitalizeFirst(key))
      .filter((name, index, arr) => name && arr.indexOf(name) === index);
    return names.length ? names.join(', ') : 'null';
  }

  private parseJsonObjectValue(value: unknown): { [key: string]: unknown } | null {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as { [key: string]: unknown };
    }
    if (typeof value === 'string' && value.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private formatAuditValues(val: Audit): Audit {
    const newVal = { ...val };

    newVal.previousValue = this.formatAuditArrayValue(newVal.previousValue);
    newVal.newValue = this.formatAuditArrayValue(newVal.newValue);

    if (newVal.previousValue === null && newVal.newValue === '') {
      newVal.previousValue = 'null';
      newVal.newValue = 'empty string';
    }
    return newVal;
  }

  private formatAuditArrayValue(value: unknown): unknown {
    if (!Array.isArray(value)) {
      return value;
    }
    return value.length === 0 ? 'null' : value.join(', ');
  }

  private adjustPublisherAuditFields(val: Audit): Audit {
    const fields = ['previousValue', 'newValue'];
    if (val.fieldName.toLowerCase().includes('country') && !val.fieldName.includes('publisherRegionCountryFilters')) {
      val.fieldName = 'country';
    }
    const configObj: any = this.publisherFieldsToUiMapping[val.fieldName];
    if (!configObj) {
      return { ...val };
    }
    if (this.TRANSLATABLE_ENTITIES_KEYS.includes(val.fieldName)) {
      fields.forEach(field => this.translateField(val, field, configObj));
    }
    if (val.fieldName === 'tsFeePctOverride') {
      this.formatPercentageField(val, false);
    }
    if (val.fieldName === 'deactivationReason') {
      this.formatDeactivationReasonField(val);
    }
    val.fieldName = configObj.displayName;
    return { ...val };
  }

  private formatDeactivationReasonField(val: Audit): void {
    val.previousValue = this.formatDeactivationReasonValue(val.previousValue);
    val.newValue = this.formatDeactivationReasonValue(val.newValue);
  }

  private formatDeactivationReasonValue(value: unknown): unknown {
    if (value == null || value === '') {
      return value;
    }
    return formatPublisherDeactivationReason(value as string);
  }

  private adjustTrafficSourceAuditFields(val: Audit): Audit {
    const configObj: any = this.trafficSourceFieldsToUiMapping[val.fieldName];
    if (!configObj) {
      return { ...val };
    }

    this.formatTrafficSourceField(val);
    val.fieldName = configObj.displayName;
    return { ...val };
  }

  private formatTrafficSourceField(val: Audit): void {
    if (val.fieldName === 'trafficSourceFeePct') {
      this.formatPercentageField(val, true);
    }
  }

  private formatPercentageField(val: Audit, parseValue: boolean): void {
    val.previousValue = this.formatPercentageValue(val.previousValue, parseValue);
    val.newValue = this.formatPercentageValue(val.newValue, parseValue);
  }

  private formatPercentageValue(value: any, parseValue: boolean): string {
    if (value === null || value === '' || value === 'N/A') {
      return 'null';
    }
    return (parseValue ? parseFloat(value) : value) + '%';
  }


  private translateArrayField(val: unknown, field: string, configObj: any): string{
    if (val[field].length === 0) {
      return 'null';
    }
    return this.translateName(configObj, val[field]);
  }

  private translateName(configObj: any, ids: string[]): string {
    const matchList = this.auditTranslatableEntities[configObj.matchListName] || [];
    const resolvedNames = this.getNamesByIds(ids, matchList, configObj.matchProp, configObj.nameProp);
    return resolvedNames.join(',');
  }

  private translateStringField(val: string, configObj: any): string {
    switch (val) {
      case this.GEO_FLOORS_ALL_COUNTRIES:
        return 'All Countries';
      case 'null':
        return 'null';
      default:
        return this.translateName(configObj, val.split(','));
    }
  }

  private translateField(val: unknown, field: string, configObj: any): void {
    if (Array.isArray(val[field])) {
      val[field] = this.translateArrayField(val, field, configObj);
    } else if (val[field]) {
      val[field] = this.translateStringField(val[field], configObj);
    } else {
      val[field] = 'null';
    }
  }

  private getNamesByIds(ids: string[], matchList: unknown[], matchProp: string, nameProp: string): string[] {
    return ids.reduce((res: string[], val: string) => {
      const match = matchList.find(v => String(v[matchProp]) === String(val));
      if (match) {
        res.push(match[nameProp]);
      }
      return res;
    }, []);
  }

  private translateIabCategories(iabCategoriesTree: NzTreeNode[]) {
    return iabCategoriesTree?.reduce((res: Array<{ key: string; title: string }>, val) => {
      if (val.children) {
        val.children.forEach(c => res.push({ key: c.key, title: c.title }));
      }
      res.push({ key: val.key, title: val.title });
      return res;
    }, []);
  }

  private loadUsers(usersType: string): void {
    const filters = this.buildUsersQueryFilters(usersType);
    this.apiService.getUsers(filters).pipe(takeUntil(this.unsubscribe$)).subscribe(res => {
      const sortedUsers = this.processSortedUsers(res.data);
      const filterIdToUpdate = usersType === 'INTERNAL' ? 'auditInternalUser' : this.entityUserFilterId;
      this.auditFilters = this.sharedService.updateFilterPossibleValues(this.auditFilters, filterIdToUpdate, sortedUsers);
    });
  }

  private buildUsersQueryFilters(usersType: string): JapiQuery {
    const entityTypeFieldName = this.getEntityTypeFieldName();
    const fieldName = usersType === 'INTERNAL' ? 'type' : entityTypeFieldName;
    const entityId = (this.auditType === 'DEAL' || this.auditType === 'DEAL_PACKAGE') ? this.entityPublisherId : this.publisherId;
    const value = usersType === 'INTERNAL' ? 'INTERNAL' : entityId;

    return {
      filter: {
        filters: [{
          fieldName,
          operation: 'EQUALS',
          value,
        }]
      }
    };
  }

  private getEntityTypeFieldName(): string {
    switch (this.auditType) {
      case 'DEAL_PARTNER':
        return 'dealPartnerId';
      default:
        return 'publisherId';
    }
  }

  private processSortedUsers(users: CtrlUser[]): any[] {
    if (!users || !users.length) {
      return [];
    }

    const sortedUsers = sortBy(users, [
      (a: CtrlUser) => a.type === 'API' ? 1 : 0,
      (a: CtrlUser) => `${a.firstName.toLowerCase()} ${a.lastName.toLowerCase()}`,
    ]);

    return sortedUsers.reduce((result, val) => {
      if (result.findIndex(f => f.value === val) === -1) {
        result.push({ id: val.id, displayName: val.firstName + ' ' + val.lastName, value: val.id });
      }
      return result;
    }, []);
  }

  onTableSortChange(table: AppTable) {
    this.auditTable.sortBy = table.sortBy;
    this.auditTable.sortDirection = table.sortDirection.toUpperCase();
    this.auditTable.pageIndex = 1;
    this.auditTable.pageSize = table.pageSize;
    if (!this.auditTable.sortBy) {
      this.auditTable.sortBy = 'timestamp';
      this.auditTable.sortDirection = 'DESC';
    }
    this.loadAudit();
  }

  onTablePageChange(table: AppTable) {
    this.auditTable.pageIndex = table.pageIndex;
    this.loadAudit();
  }

  onButtonClick(buttonId: string) {
    switch (buttonId) {
      case 'auditResetFiltersButton':
        this.onFiltersChange({ filters: this.auditFilters, filterId: 'n/a' }, true);
        break;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
